import 'react-loading-skeleton/dist/skeleton.css'

import { Link } from 'react-router-dom'
import styled from 'styled-components'

import newsIcon from '@/assets/images/icon_news_green.png'
import noticeIcon from '@/assets/images/icon_notice_green.png'
import { CardListSkeleton, ItemListSkeleton } from '@/shared/components/common/Skeleton'
import useMediaQuery from '@/shared/hooks/useMediaQuery'
import MetaTag from '@/shared/seo/MetaTag'
import { NewsItemType, NoticeItemType } from '@/shared/types/api'
import { Nanum_r_size, Nanum_rb_size } from '@/styles/font/NanumSquare'
import { mediaMobile, mediaTablet } from '@/styles/mediaQuery'
import { bgSet, icoSet } from '@/styles/mixins'

interface MainNoticeAndNewsSectionProps {
  newsData: NewsItemType[]
  noticeData: NoticeItemType[]
  isLoading: boolean
}
const MainNoticeAndNewsSection = ({
  newsData,
  noticeData,
  isLoading,
}: MainNoticeAndNewsSectionProps) => {
  const { isMobile } = useMediaQuery()
  const IMG_BASE_URL = import.meta.env.VITE_APP_SERVER_IMG_URL

  return (
    <>
      <MetaTag
        newsList={newsData?.map((item) => ({
          NewsTitle: item.NewsTitle,
          RegDT: item.RegDT,
        }))}
        noticeList={noticeData?.map((item) => ({
          Title: item.Title,
          RegDT: item.RegDT,
        }))}
      />
      <Section>
        <NoticeWrap>
          {isLoading ? (
            <ItemListSkeleton count={isMobile ? 2 : 5} />
          ) : (
            <>
              <Heading>
                <HeadingTitle className="notice">공지사항</HeadingTitle>
                <MoreBtn to="/notice">더보기</MoreBtn>
              </Heading>
              <NoticeList>
                {noticeData?.map((notice) => (
                  <NoticeItem key={notice.Idx}>
                    <NoticeTitle to={`/notice/detail/${notice.Idx}`}>{notice.Title}</NoticeTitle>
                  </NoticeItem>
                ))}
              </NoticeList>
            </>
          )}
        </NoticeWrap>
        <NewsWrap>
          {isLoading ? (
            <CardListSkeleton count={isMobile ? 2 : 4} />
          ) : (
            <>
              <Heading>
                <HeadingTitle className="news">새소식</HeadingTitle>
                <MoreBtn to="/news">더보기</MoreBtn>
              </Heading>
              <NewsList>
                {newsData.slice(0, 4).map((news) => (
                  <NewsItem key={news.Idx}>
                    <a href={news.LinkPath} target="_blank" rel="noreferrer">
                      <img src={`${IMG_BASE_URL}${news.ImgPath}`} alt={news.NewsTitle} />
                      <NewsHeading>
                        <NewsType>{news.NewsType === 1 ? 'Blog' : 'NEWS'}</NewsType>
                        <NewsTitle>{news.NewsTitle}</NewsTitle>
                      </NewsHeading>
                    </a>
                  </NewsItem>
                ))}
              </NewsList>
            </>
          )}
        </NewsWrap>
      </Section>
    </>
  )
}

export default MainNoticeAndNewsSection

const Section = styled.section`
  display: flex;
  padding: 35px 0 145px;

  ${mediaTablet} {
    flex-direction: column;
    gap: 30px;
    padding: 72px 0;
  }

  ${mediaMobile} {
    padding-left: 24px;
    padding-right: 24px;
  }
`

const Heading = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 17px;

  ${mediaTablet} {
    margin-bottom: 15px;
  }
`
const HeadingTitle = styled.h4`
  ${Nanum_rb_size(22)}

  &:before {
    content: '';
    display: inline-block;
    width: 25px;
    height: 25px;
    vertical-align: middle;
    margin-right: 14px;
    ${icoSet}
  }

  &.notice::before {
    background-image: url(${noticeIcon});
  }

  &.news::before {
    background-image: url(${newsIcon});
  }
`
const NoticeWrap = styled.div`
  flex: 0 0 42.5%;
  max-width: 510px;

  ${mediaTablet} {
    width: 100%;
    max-width: none;
  }
`
const MoreBtn = styled(Link)`
  ${Nanum_rb_size(16)}
  color: var(--green);
  background-color: var(--green-2);
  border-radius: 30px;
  padding: 8px 12px 8px 12px;

  &:after {
    content: '';
    display: inline-block;
    width: 14px;
    height: 14px;
    vertical-align: middle;
    margin-left: 14px;
    background-image: url('/assets/icons/icon_more_green.svg');
    ${icoSet}
  }
`
const NoticeTitle = styled(Link)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`
const NoticeList = styled.ul`
  margin-top: 30px;

  ${mediaTablet} {
    margin-top: 0px;
  }
`
const NoticeItem = styled.li`
  ${Nanum_r_size(18)}
  border-bottom: 1px solid var(--gray-d5);

  &:first-child {
    border-top: 1px solid var(--gray-d5);
  }

  ${mediaTablet} {
    &:nth-child(n + 5) {
      display: none;
    }
  }

  ${mediaMobile} {
    &:not(:first-child) {
      display: none;
    }
  }

  a {
    padding: 23px 0;
    display: block;
    height: 100%;

    ${mediaTablet} {
      padding: 23px 17px;
    }
  }
`

const NewsWrap = styled.div`
  flex: 0 0 57.5%;
  padding-left: 60px;

  ${mediaTablet} {
    padding-left: 0px;
    width: 100%;
  }
`
const NewsList = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  ${mediaTablet} {
    gap: 14px;
  }

  ${mediaMobile} {
    grid-template-columns: repeat(1, 1fr);
  }
`
const NewsItem = styled.li`
  border-radius: 10px;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.4);
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    ${bgSet}
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 50%;
    background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.76));
    z-index: 1;
    pointer-events: none;
  }

  ${mediaMobile} {
    &:not(:nth-child(-n + 2)) {
      display: none;
    }
  }
`
const NewsHeading = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  padding: 0 16px 16px;
  color: var(--white);
`
const NewsType = styled.div`
  ${Nanum_rb_size(16)}
  font-weight: 800;
  border: 2px solid var(--white);
  border-radius: 30px;
  width: fit-content;
  height: 24px;
  padding: 2px 12px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;

  ${mediaTablet} {
    ${Nanum_rb_size(14)}
  }
`
const NewsTitle = styled.strong`
  ${Nanum_rb_size(18)}

  ${mediaTablet} {
    ${Nanum_rb_size(16)}
  }
`
